/**
 * Timesheets: what you worked on this week, and a manager's sign-off queue.
 *
 * Distinct from Attendance, which answers whether you were present, and from
 * the Calls tracker, which answers what recruiting activity happened. This
 * answers how many hours, against what, approved by whom.
 *
 * The week is never named by this screen. It sends a date it wants a sheet
 * *for* and the server returns the Monday-to-Sunday week containing it, so
 * navigation walks off the period the server returned rather than
 * re-implementing the boundary here and risking a different answer.
 *
 * Two permissions overlap, and each control is gated to what the API allows
 * rather than to the page:
 *
 *   filing and editing - the owner of the sheet, or an approver on their behalf
 *   approving          - managers and above, never on your own sheet
 *   reopening          - company admin and the owner only
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { isCompanyAdmin, isSupervisor, useAuth } from '../store/auth';
import type {
  AttendanceConflict,
  Client as ClientRecord,
  Page,
  Timesheet,
  TimesheetDetail,
  TimesheetEntry,
  TimesheetStatus,
} from '../types';

const STATUS_TONE: Record<TimesheetStatus, string> = {
  draft: 'bg-gray-200 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

/** Why a day with booked hours is worth a second look, in plain words. */
const CONFLICT_REASON: Record<string, string> = {
  absent: 'attendance scored this day absent',
  leave: 'this day is approved leave',
  holiday: 'this is a company holiday',
  weekly_off: 'this is a weekly off',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Shift an ISO date by whole days.
 *
 * Parsed as UTC on purpose. `new Date('2026-08-17')` is already UTC midnight,
 * but `new Date(2026, 7, 17)` is local midnight, and mixing the two shifts the
 * date by one either side of the international date line.
 */
function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${DAY_NAMES[d.getUTCDay()]} ${iso.slice(8)}`;
}

/** Every date from start to end, inclusive at both ends. */
function daysBetween(start: string, end: string): string[] {
  const out: string[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) out.push(day);
  return out;
}

function formatHours(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : value;
}

export default function TimesheetsPage() {
  const me = useAuth((s) => s.user);
  const canApprove = isSupervisor(me);

  // The date we want a sheet *for*. The server turns it into a week.
  const [weekOf, setWeekOf] = useState(todayIso);

  const sheet = useQuery<TimesheetDetail>({
    queryKey: ['timesheet', weekOf],
    // POST because opening a week is how the API exposes "get or create", and
    // it is idempotent by design. Refetch-on-focus is off so an idle tab does
    // not fire writes nobody asked for.
    queryFn: async () =>
      (await client.post('/api/v1/timesheets', { weekOf })).data,
    refetchOnWindowFocus: false,
  });

  const period = sheet.data;

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Timesheets</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              Record what you worked on each day and submit the week for
              approval. Attendance answers whether you were here; this answers
              what the time went to. Hours are a record of work — they do not
              change your pay.
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              className="btn-ghost px-2"
              aria-label="Previous week"
              disabled={!period}
              onClick={() => period && setWeekOf(addDays(period.periodStart, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="btn-ghost whitespace-nowrap text-xs"
              onClick={() => setWeekOf(todayIso())}
            >
              This week
            </button>
            <button
              type="button"
              className="btn-ghost px-2"
              aria-label="Next week"
              disabled={!period}
              onClick={() => period && setWeekOf(addDays(period.periodEnd, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {sheet.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : sheet.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load this week.</p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void sheet.refetch()}
          >
            Retry
          </button>
        </div>
      ) : period ? (
        <WeekSheet sheet={period} weekKey={weekOf} />
      ) : null}

      {canApprove && <ApprovalQueue />}
    </div>
  );
}

function WeekSheet({ sheet, weekKey }: { sheet: TimesheetDetail; weekKey: string }) {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const canReopen = isCompanyAdmin(me);

  const [adding, setAdding] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['timesheet', weekKey] });
    queryClient.invalidateQueries({ queryKey: ['timesheet-queue'] });
  };

  const submit = useMutation({
    mutationFn: async () =>
      (await client.post(`/api/v1/timesheets/${sheet.id}/submit`)).data,
    onSuccess: () => {
      invalidate();
      toast.success('Week submitted for approval');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const reopen = useMutation({
    mutationFn: async () =>
      (await client.post(`/api/v1/timesheets/${sheet.id}/reopen`, { note: null }))
        .data,
    onSuccess: () => {
      invalidate();
      toast.success('Week reopened for correction');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const removeEntry = useMutation({
    mutationFn: async (entryId: string) =>
      client.delete(`/api/v1/timesheets/${sheet.id}/entries/${entryId}`),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const days = useMemo(
    () => daysBetween(sheet.periodStart, sheet.periodEnd),
    [sheet.periodStart, sheet.periodEnd],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, TimesheetEntry[]>();
    for (const entry of sheet.entries) {
      const list = map.get(entry.workDate) ?? [];
      list.push(entry);
      map.set(entry.workDate, list);
    }
    return map;
  }, [sheet.entries]);

  const conflictByDay = useMemo(() => {
    const map = new Map<string, AttendanceConflict>();
    for (const c of sheet.attendanceConflicts) map.set(c.workDate, c);
    return map;
  }, [sheet.attendanceConflicts]);

  return (
    <section className="space-y-3">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <CalendarRange className="h-4 w-4 text-gray-400" />
              {sheet.periodStart} → {sheet.periodEnd}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {sheet.userName ?? 'You'} ·{' '}
              <span className="tabular-nums">{formatHours(sheet.totalHours)}</span>{' '}
              hours
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className={`badge ${STATUS_TONE[sheet.status]}`}>
              {sheet.status}
            </span>
            {sheet.isEditable && sheet.entries.length > 0 && (
              <button
                type="button"
                className="btn-secondary"
                disabled={submit.isPending}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? 'Submitting…' : 'Submit week'}
              </button>
            )}
            {/* Approved is terminal. Only a company admin can undo it, and the
                API records the reopen — a manager cannot rewrite their own
                sign-off. */}
            {sheet.status === 'approved' && canReopen && (
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={reopen.isPending}
                onClick={() => reopen.mutate()}
              >
                Reopen
              </button>
            )}
          </div>
        </div>

        {sheet.decisionNote && (
          <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <span className="font-medium">Approver&rsquo;s note:</span>{' '}
            {sheet.decisionNote}
          </p>
        )}

        {sheet.status === 'rejected' && (
          <p className="mt-2 text-xs text-red-700">
            Sent back for correction. Fix the entries below and submit again.
          </p>
        )}

        {sheet.attendanceConflicts.length > 0 && (
          <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {sheet.attendanceConflicts.length}{' '}
            {sheet.attendanceConflicts.length === 1 ? 'day is' : 'days are'} flagged
            below because attendance did not score them as worked. That is a
            prompt to check, not a blocker — attendance is derived from presence
            pings, which a flat battery is enough to break.
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
        {days.map((day) => {
          const entries = byDay.get(day) ?? [];
          const conflict = conflictByDay.get(day);
          const total = entries.reduce((sum, e) => sum + Number(e.hours), 0);

          return (
            <div key={day} className="border-b border-gray-100 last:border-b-0">
              <div className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-medium text-gray-700">
                    {dayLabel(day)}
                  </span>
                  {total > 0 && (
                    <span className="text-xs tabular-nums text-gray-500">
                      {total.toFixed(2)} h
                    </span>
                  )}
                  {conflict && (
                    <span
                      className="badge bg-amber-100 text-amber-700"
                      title={CONFLICT_REASON[conflict.attendanceStatus] ?? ''}
                    >
                      {CONFLICT_REASON[conflict.attendanceStatus] ??
                        conflict.attendanceStatus}
                    </span>
                  )}
                </div>

                {sheet.isEditable && (
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs"
                    onClick={() => setAdding(adding === day ? null : day)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </button>
                )}
              </div>

              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start justify-between gap-3 border-t border-gray-50 bg-gray-50/50 px-4 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="tabular-nums">
                        {formatHours(entry.hours)} h
                      </span>
                      {entry.clientName && (
                        <span className="ml-2 text-gray-600">
                          {entry.clientName}
                        </span>
                      )}
                      {entry.activity && (
                        <span className="ml-2 text-xs text-gray-500">
                          {entry.activity}
                        </span>
                      )}
                    </p>
                    {entry.description && (
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {entry.description}
                      </p>
                    )}
                  </div>

                  {sheet.isEditable && (
                    <button
                      type="button"
                      className="btn-ghost px-2 py-1 text-xs text-red-600"
                      aria-label="Delete entry"
                      disabled={removeEntry.isPending}
                      onClick={() => removeEntry.mutate(entry.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}

              {adding === day && (
                <EntryForm
                  sheetId={sheet.id}
                  workDate={day}
                  onDone={() => {
                    setAdding(null);
                    invalidate();
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {sheet.entries.length === 0 && (
        <div className="card text-center">
          <Clock className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            Nothing booked this week yet.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Add time to a day above. A week with no entries cannot be submitted.
          </p>
        </div>
      )}
    </section>
  );
}

function EntryForm({
  sheetId,
  workDate,
  onDone,
}: {
  sheetId: string;
  workDate: string;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    hours: '8',
    clientId: '',
    activity: '',
    description: '',
  });

  const clients = useQuery<Page<ClientRecord>>({
    queryKey: ['timesheet-clients'],
    queryFn: async () =>
      (await client.get('/api/v1/clients', { params: { limit: 200 } })).data,
  });

  const add = useMutation({
    mutationFn: async () =>
      (
        await client.post(`/api/v1/timesheets/${sheetId}/entries`, {
          workDate,
          hours: form.hours,
          clientId: form.clientId || null,
          activity: form.activity.trim() || null,
          description: form.description.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Time booked');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <form
      className="space-y-3 border-t border-gray-100 bg-white px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <div>
          <label className="label" htmlFor={`hours-${workDate}`}>
            Hours
          </label>
          <input
            id={`hours-${workDate}`}
            className="input"
            type="number"
            /* Quarter-hour steps, matching what the server stores. Anything
               finer is rounded there rather than silently kept. */
            step="0.25"
            min="0.25"
            max="24"
            required
            value={form.hours}
            onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
          />
        </div>

        <div>
          <label className="label" htmlFor={`client-${workDate}`}>
            Client
          </label>
          <select
            id={`client-${workDate}`}
            className="input"
            value={form.clientId}
            onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
          >
            <option value="">Internal — no client</option>
            {(clients.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor={`activity-${workDate}`}>
            Activity
          </label>
          <input
            spellCheck={true}
            id={`activity-${workDate}`}
            className="input"
            maxLength={120}
            placeholder="Sourcing, client call…"
            value={form.activity}
            onChange={(e) => setForm((f) => ({ ...f, activity: e.target.value }))}
          />
        </div>

        <div>
          <label className="label" htmlFor={`desc-${workDate}`}>
            What you did
          </label>
          <input
            spellCheck={true}
            id={`desc-${workDate}`}
            className="input"
            maxLength={2000}
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost text-xs" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn-secondary" disabled={add.isPending}>
          {add.isPending ? 'Saving…' : 'Book time'}
        </button>
      </div>
    </form>
  );
}

function ApprovalQueue() {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.user);
  const [deciding, setDeciding] = useState<{
    sheet: Timesheet;
    action: 'approve' | 'reject';
  } | null>(null);

  const queue = useQuery<Page<Timesheet>>({
    queryKey: ['timesheet-queue'],
    queryFn: async () =>
      (
        await client.get('/api/v1/timesheets', {
          params: { status: 'submitted', limit: 200 },
        })
      ).data,
  });

  const rows = queue.data?.items ?? [];

  return (
    <section className="card">
      <h3 className="text-sm font-semibold text-gray-900">Waiting for approval</h3>
      <p className="mt-1 max-w-2xl text-xs text-gray-500">
        Weeks your team has submitted. Approving is final — correcting an
        approved week afterwards takes a company administrator.
      </p>

      {queue.isLoading ? (
        <div className="flex justify-center py-6 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">Nothing waiting.</p>
      ) : (
        <div className="mt-3 divide-y divide-gray-100">
          {rows.map((row) => {
            const mine = row.userId === me?.id;
            return (
              <div
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div>
                  <p className="text-sm text-gray-900">
                    {row.userName ?? row.userId}
                    {mine && (
                      <span className="ml-1.5 text-xs text-gray-400">you</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {row.periodStart} → {row.periodEnd} ·{' '}
                    <span className="tabular-nums">
                      {formatHours(row.totalHours)}
                    </span>{' '}
                    hours
                  </p>
                </div>

                {/* Nobody signs off their own week; the API refuses it, so the
                    buttons are not offered. */}
                {mine ? (
                  <span className="text-xs text-gray-400">
                    Someone else must approve this
                  </span>
                ) : (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="btn-ghost px-2 py-1 text-xs"
                      onClick={() => setDeciding({ sheet: row, action: 'approve' })}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn-ghost px-2 py-1 text-xs text-red-600"
                      onClick={() => setDeciding({ sheet: row, action: 'reject' })}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {deciding && (
        <DecisionDialog
          sheet={deciding.sheet}
          action={deciding.action}
          onClose={() => setDeciding(null)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['timesheet-queue'] });
            queryClient.invalidateQueries({ queryKey: ['timesheet'] });
            setDeciding(null);
          }}
        />
      )}
    </section>
  );
}

function DecisionDialog({
  sheet,
  action,
  onClose,
  onDone,
}: {
  sheet: Timesheet;
  action: 'approve' | 'reject';
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');

  // The detail is fetched here rather than in the queue, so the approver sees
  // the days and any attendance disagreement at the moment of deciding — not
  // a total with nothing behind it.
  const detail = useQuery<TimesheetDetail>({
    queryKey: ['timesheet-detail', sheet.id],
    queryFn: async () => (await client.get(`/api/v1/timesheets/${sheet.id}`)).data,
  });

  const decide = useMutation({
    mutationFn: async () =>
      (
        await client.post(`/api/v1/timesheets/${sheet.id}/${action}`, {
          note: note.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success(action === 'approve' ? 'Week approved' : 'Week sent back');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const conflicts = detail.data?.attendanceConflicts ?? [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lg">
        <h4 className="text-sm font-semibold text-gray-900">
          {action === 'approve' ? 'Approve' : 'Send back'} {sheet.periodStart} →{' '}
          {sheet.periodEnd}
        </h4>
        <p className="mt-1 text-xs text-gray-500">
          {sheet.userName ?? sheet.userId} ·{' '}
          <span className="tabular-nums">{formatHours(sheet.totalHours)}</span>{' '}
          hours
        </p>

        {detail.isLoading ? (
          <div className="flex justify-center py-4 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {conflicts.length > 0 && (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2">
                <p className="flex items-start gap-1.5 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Attendance disagrees on {conflicts.length}{' '}
                  {conflicts.length === 1 ? 'day' : 'days'}. Worth asking about,
                  but not a reason on its own to send the week back.
                </p>
                <ul className="mt-1.5 space-y-0.5 pl-5 text-xs text-amber-900">
                  {conflicts.map((c) => (
                    <li key={c.workDate}>
                      {c.workDate} — {formatHours(c.hours)} h booked,{' '}
                      {CONFLICT_REASON[c.attendanceStatus] ?? c.attendanceStatus}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ul className="mt-3 max-h-48 space-y-1 overflow-y-auto text-xs text-gray-600">
              {(detail.data?.entries ?? []).map((e) => (
                <li key={e.id} className="flex justify-between gap-3">
                  <span className="truncate">
                    {dayLabel(e.workDate)} · {e.clientName ?? 'Internal'}
                    {e.activity && ` · ${e.activity}`}
                  </span>
                  <span className="tabular-nums">{formatHours(e.hours)} h</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <label className="label mt-4 block" htmlFor="decision-note">
          Note{' '}
          {action === 'reject' && (
            <span className="font-normal text-gray-500">
              — say what needs fixing
            </span>
          )}
        </label>
        <textarea
          spellCheck={true}
          id="decision-note"
          className="input"
          rows={2}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={decide.isPending}
            onClick={() => decide.mutate()}
          >
            {decide.isPending
              ? 'Saving…'
              : action === 'approve'
                ? 'Approve week'
                : 'Send back'}
          </button>
        </div>
      </div>
    </div>
  );
}
